import './index.css';

/**
 * Boot the app with a visible failure mode.
 *
 * The application code is loaded as a separate chunk on purpose: if anything in it
 * fails to load or evaluate (missing file, crash during module init), we can catch it
 * and show the real reason instead of leaving a blank white page.
 */

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

function showFatal(root: HTMLElement, error: unknown) {
  const detail =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : 'The application failed to start.';

  const rawMessage = detail.toLowerCase();
  const hint =
    rawMessage.includes('supabaseurl') || rawMessage.includes('supabasekey')
      ? 'This build was given an unusable Backend address. Set VITE_PUBLIC_SUPABASE_URL to a full https:// address (no quotes, no trailing slash) and redeploy — these values are baked in at build time.'
      : '';

  console.error('[AbeniGele] Failed to start:', error);

  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#fbf7ef;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;color:#241d16;">
      <div style="width:100%;max-width:520px;border:1px solid #e6dccb;border-radius:10px;background:#fffdf9;padding:28px;">
        <h1 style="margin:0;font-size:19px;font-weight:600;">The site could not finish starting</h1>
        <p style="margin:8px 0 0;font-size:14px;line-height:1.6;color:#5b5148;">
          The page loaded but the application script did not run. This message is here for diagnosis.
        </p>
        <pre style="margin:16px 0 0;padding:12px;border-radius:6px;background:#f3ece1;font-size:12px;line-height:1.5;white-space:pre-wrap;word-break:break-word;">${escapeHtml(
          detail,
        )}</pre>
        ${
          hint
            ? `<p style="margin:12px 0 0;font-size:13px;line-height:1.6;color:#5b5148;">${escapeHtml(
                hint,
              )}</p>`
            : ''
        }
        <button type="button" onclick="window.location.reload()" style="margin-top:20px;width:100%;padding:10px 16px;border:0;border-radius:6px;background:#3b2f24;color:#fffdf9;font-size:14px;cursor:pointer;">Reload page</button>
      </div>
    </div>
  `;
}

async function boot() {
  const rootElement = document.getElementById('root');
  if (!rootElement) return;

  try {
    const [{ createElement, StrictMode }, { createRoot }, { default: App }, { default: AppErrorBoundary }] =
      await Promise.all([
        import('react'),
        import('react-dom/client'),
        import('./App.tsx'),
        import('@/components/base/AppErrorBoundary'),
      ]);

    createRoot(rootElement).render(
      createElement(
        StrictMode,
        null,
        createElement(AppErrorBoundary, null, createElement(App)),
      ),
    );
  } catch (error) {
    showFatal(rootElement, error);
  }
}

void boot();